import { Card, Stack, Text, Button, Group, ThemeIcon } from '@/components/ui';
import { IconPlugConnected, IconClockPause } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';

interface Props {
  /** True when no accounts are connected at all. */
  noAccounts: boolean;
  /** What this page would show once data arrives. */
  subject?: string;
}

/**
 * The state this section is genuinely in until accounts are connected and Meta App
 * Review completes.
 *
 * Distinguishes "nothing connected yet" from "connected but no data in this window" --
 * the first needs an action, the second just needs patience or a wider range. Showing a
 * chart of zeroes for either would misrepresent both.
 */
export function SocialEmptyState({ noAccounts, subject = 'data' }: Props) {
  const navigate = useNavigate();

  if (noAccounts) {
    return (
      <Card shadow="sm" padding="xl" radius="md" withBorder>
        <Stack align="center" gap="sm">
          <ThemeIcon size={48} radius="xl" variant="light" color="blue">
            <IconPlugConnected size={26} />
          </ThemeIcon>
          <Text fw={600}>No social media accounts connected</Text>
          <Text size="sm" c="dimmed" ta="center" maw={460}>
            Connect a Facebook Page, Instagram Business account or YouTube channel to start
            collecting analytics. The sync runs every 30 minutes once an account is authorised.
          </Text>
          <Group mt="xs">
            <Button onClick={() => navigate('/app/social-media/admin/accounts')}>
              Manage accounts
            </Button>
          </Group>
        </Stack>
      </Card>
    );
  }

  return (
    <Card shadow="sm" padding="xl" radius="md" withBorder>
      <Stack align="center" gap="sm">
        <ThemeIcon size={48} radius="xl" variant="light" color="gray">
          <IconClockPause size={26} />
        </ThemeIcon>
        <Text fw={600}>No {subject} for this period</Text>
        <Text size="sm" c="dimmed" ta="center" maw={460}>
          Accounts are connected, but nothing was recorded in the selected date range.
          Try a wider range, or check the sync status if this looks wrong.
        </Text>
      </Stack>
    </Card>
  );
}
